import { IMdlCustomDialogConfiguration, MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { UrlBuilder } from '../../../core';
import { FileFormArray } from '../../../covance-file-upload/file-form-array';
import { EQueryQuestionInfo } from '../e-query-details/questions/e-query-question-info';
import { ACCESSION_SUBJECT_TOKEN, ACTIVE_EQUERY_TOKEN, AdminEQueryPreviewDialogComponent, EQUERY_EDIT_TOKEN } from '../e-query-preview/admin-e-query-preview-dialog.component';
import { ActiveEQuery } from '../models/active-e-query';
import { EQueryCreateData, EQueryQuestionCreateData } from '../models/e-query-create-data';
import { EQueryStageData, QueryContactCenter } from '../models/e-query-stage';
import { EQueryAttachmentsUploaderComponent } from './e-query-attachments-uploader/e-query-attachments-uploader.component';
import { UserStorage } from '../../../user';

@Component({
    selector: 'admin-prefilled-active-e-query-edit',
    template: require('./admin-prefilled-active-e-query-edit.component.html'),
    styles: [require('./admin-prefilled-active-e-query-edit.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPrefilledActiveEQueryEditComponent {
    @Input()
    public activeEQuery: ActiveEQuery;

    @Input()
    public eQueryStage: EQueryStageData;

    @ViewChild('attachmentsUploader')
    public attachmentsUploader: EQueryAttachmentsUploaderComponent;

    public readonly editorForm: FormGroup;
    public readonly fileFormArray = new FileFormArray(false);

    constructor(
        private readonly dialogService: MdlDialogService,
        private readonly urlBuilder: UrlBuilder,
        private readonly userStorage: UserStorage,
    ) {
        this.editorForm = new FormGroup(
            {
                'files': this.fileFormArray,
            },
        );
    }

    public async save() {
        const configDialog: IMdlCustomDialogConfiguration = {
            component: AdminEQueryPreviewDialogComponent,
            providers: [
                {
                    provide: EQUERY_EDIT_TOKEN, useValue: this.getMergeEQueryData(this.eQueryStage)
                },
                {
                    provide: ACCESSION_SUBJECT_TOKEN, useValue: this.eQueryStage.accessionSubject
                },
                {
                    provide: ACTIVE_EQUERY_TOKEN, useValue: this.activeEQuery,
                }
            ],
            isModal: true,
            clickOutsideToClose: true,
            classes: 'e-query-preview-dialog'
        };

        const dialogRef = await this.dialogService.showCustomDialog(configDialog).toPromise();
        const isSubmitted = await dialogRef.onHide().toPromise<boolean>();
        if (isSubmitted) {
            await this.navigateToSummaryTab();
        }
    }

    public async cancel() {
        try {
            await this.dialogService.confirm('Do you want to cancel this eQuery?', 'No', 'Yes').toPromise();
            await this.navigateToSummaryTab();
            return true;
        } catch {
            return false;
        }
    }

    public mapPrefilledQuestionsToQuestionsInfos(prefilledQuestions: QueryContactCenter[]): EQueryQuestionInfo[] {
        return prefilledQuestions.map<EQueryQuestionInfo>(pq => ({
            queryText: pq.query,
            issueTypeDescription: pq.issueDesc,
            issueType: pq.issueType
        }));
    }

    private async getEQueryUrlPart() {
        const currentUser = await this.userStorage.currentUser.toPromise();
        if (currentUser.isAdmin) {
            return this.urlBuilder
                .esa()
                .admin()
                .adminEQuery();
        }

        if (currentUser.isInternal) {
            return this.urlBuilder
                .esa()
                .admin()
                .coordinatorEQuery();
        }

        throw new Error('Access denied.');
    }

    private async navigateToSummaryTab() {
        const urlPart = await this.getEQueryUrlPart();
        await urlPart
            .eQuerySummary()
            .navigate();
    }

    private getMergeEQueryData(eQueryStage: EQueryStageData): EQueryCreateData {
        const files = this.attachmentsUploader.getFilesWithDescriptions();

        const eQueryData: EQueryCreateData = {
            projectId: eQueryStage.projectId,
            commentText: null,
            accessionId: eQueryStage.accessionSubject.accessionId,
            escalationMails1: [],
            escalationMails2: [],
            escalationMailsSite: [],
            files: files,
            questions: eQueryStage.queries.map<EQueryQuestionCreateData>(q => {
                return  {
                    issueType: q.issueType,
                    issueDescription: q.issueDesc,
                    queryText: q.query,
                    responseMask: q.mask,
                    responseMaskDescription: q.maskDesc
                };
            }),
            currentActiveEQueryId: this.activeEQuery.eQueryId,
        };
        return eQueryData;
    }
}
